import { create } from 'zustand';

/**
 * LiveAnnouncer (PRD §4.8, T-11): two visually hidden live regions that are in
 * the DOM from the first render, so a screen reader announces even the first
 * message. Polite for success / info / counts; assertive for errors.
 *
 *   announce('נמצאו 7 תוצאות');
 *   announce('השמירה נכשלה', 'assertive');
 *
 * Mount <LiveAnnouncer /> once at the app root.
 */
export type Politeness = 'polite' | 'assertive';

interface AnnouncerState {
  polite: string;
  assertive: string;
}

const useAnnouncerStore = create<AnnouncerState>(() => ({ polite: '', assertive: '' }));

const timers: Partial<Record<Politeness, ReturnType<typeof setTimeout>>> = {};

/** Read `message` to screen-reader users. Repeating the same text is announced again. */
export function announce(message: string, politeness: Politeness = 'polite') {
  if (!message) return;
  // Clear first, then set on the next tick: a region whose text does not change
  // is not re-read, and some screen readers skip a change made in the same frame.
  useAnnouncerStore.setState({ [politeness]: '' } as Partial<AnnouncerState>);
  const pending = timers[politeness];
  if (pending) clearTimeout(pending);
  timers[politeness] = setTimeout(() => {
    useAnnouncerStore.setState({ [politeness]: message } as Partial<AnnouncerState>);
  }, 50);
}

export default function LiveAnnouncer() {
  const polite = useAnnouncerStore((s) => s.polite);
  const assertive = useAnnouncerStore((s) => s.assertive);
  return (
    <>
      <div className="sr-only" aria-live="polite" aria-atomic="true" data-live-announcer="polite">{polite}</div>
      <div className="sr-only" aria-live="assertive" aria-atomic="true" data-live-announcer="assertive">{assertive}</div>
    </>
  );
}
